﻿import React, { Fragment, useState, useEffect } from 'react';
import { FormSwitch, FormNumericInput } from "components/form-fields";
import { ShowHide } from "components/layout";
import { useValidation } from "hooks/useValidation";
import PropTypes from 'prop-types';
import { ReoccurrenceSettings } from './ReoccurrenceSettings';
import { NotificationInline } from 'components/notifications';
import 'components/settings/settings.css';

export function PolicySettings(
    {
        disabled = false,
        saving = false,
        reoccurrenceSettingsDisabled = false,
        editing,
        loading,
        onSettingsUpdate,
        onValidationUpdate,
        policy,
        reoccurrenceSettingsDisabledNotification
    }
) {
    const [isValid, errors, validate] = useValidation();
    const [loadingValue, setLoading] = useState(true);
    const [policySettings, setPolicySettings] = useState(
        {
            startDate: "",
            resendSchedule: {
                reoccurring: false
            },
            useAllLocations: true,
            sendDebriefAlert: true,
            debriefAlertDelayDays: 0
        });

    const [reoccurrenceValid, setReoccurrenceValid] = useState(false);

    const maxAlertDelayDays = 365;

    useEffect(onValidationChange, [isValid, reoccurrenceValid]);

    function onValidationChange() {
        onValidationUpdate(isValid && reoccurrenceValid);
    }

    function onReoccurrenceValidationUpdate(isReoccurrenceValid) {
        setReoccurrenceValid(isReoccurrenceValid);
    }

    useEffect(() => {
        setPolicySettings(p => ({
            ...p,
            ...policy
        }));
    }, [policy]);

    useEffect(() => {
        setLoading(loading);
    }, [loading]);

    function onAlertDateDelayChange(value) {

        var updatedPolicySettings = {
            ...policySettings,
            debriefAlertDelayDays: value
        }
        setPolicySettings(updatedPolicySettings);
        onSettingsUpdate(updatedPolicySettings);
    }    

    function onReoccurrenceChange(item) {
        var updatedPolicySettings = {
            ...policySettings,
            resendSchedule: item
        }
        setPolicySettings(updatedPolicySettings);
        onSettingsUpdate(updatedPolicySettings);
    }

    function onSendDebriefAlertChange(event) {

        var updatedPolicySettings = {
                ...policySettings,
                sendDebriefAlert: event.target.checked
            }

        if (!updatedPolicySettings.sendDebriefAlert) {
            updatedPolicySettings.debriefAlertDelayDays = 0;
        }
        setPolicySettings(updatedPolicySettings);
        onSettingsUpdate(updatedPolicySettings);
    }
    
    useEffect(updateValidationRules, [policy])

    function updateValidationRules() {
        let rules = [];

        if (!editing) {
            rules.push({ fieldName: "startDate", required: true });
            rules.push({ fieldName: "debriefAlertDelayDays", required: true, minValue: 0,  maxValue: maxAlertDelayDays });            
        }

        validate(rules, policy);
    }

    return (
        <Fragment>
            <ReoccurrenceSettings
                loading={loadingValue}
                reoccurrenceSettings={policySettings.resendSchedule}
                reoccurrenceToggleText="Reoccurring sign off"
                repeatEveryHeader="Sign off frequency"
                onReoccurrenceUpdate={onReoccurrenceChange}
                hideEndDates={true}
                hide445={true}
                onValidationUpdate={onReoccurrenceValidationUpdate}
                name="policy"
                startDate={policySettings.startDate}
                disabled={disabled || reoccurrenceSettingsDisabled || saving}
            ></ReoccurrenceSettings>
            <NotificationInline
                id="notification-settings-disabled"
                loading={loadingValue}
                show={reoccurrenceSettingsDisabled}
                text={reoccurrenceSettingsDisabledNotification}
                intent="info" />
            <FormSwitch
                disabled={disabled || saving}
                loading={loadingValue}
                label="Policy sign-off alert"
                checked={policySettings.sendDebriefAlert}
                helperText="This will raise a policy sign-off alert for the drivers."
                onChange={onSendDebriefAlertChange}>
            </FormSwitch>
            <ShowHide evaluator={policySettings.sendDebriefAlert }
                show={(
                    <FormNumericInput
                        disabled={disabled || saving}
                        min={0}
                        max={maxAlertDelayDays}
                        selectedValue={policySettings.debriefAlertDelayDays}
                        onValueChange={onAlertDateDelayChange}
                        headingText={"Policy sign-off alert delay in days"}
                        helperText={(<p>These are the number of days you wish to wait before the policy sign-off alert is sent. This can not exceed 365 days.</p>)}
                        dangerHelperText={errors.debriefAlertDelayDays}
                        loading={loadingValue}
                    ></FormNumericInput>
                )}>
            </ShowHide>
        </Fragment>
    );
}

PolicySettings.propTypes = {
    policy: PropTypes.object.isRequired,
    disabled: PropTypes.bool,
    saving: PropTypes.bool,
    reoccurrenceSettingsDisabled: PropTypes.bool,
    loading: PropTypes.bool.isRequired,
    onSettingsUpdate: PropTypes.func.isRequired,
    onValidationUpdate: PropTypes.func.isRequired,
    editing: PropTypes.bool,
    reoccurrenceSettingsDisabledNotification: PropTypes.string
};